import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";

export interface ConfirmAction {
  label: string;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  sx?: object;
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message: string | React.ReactNode;
  actions: ConfirmAction[];
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseButton?: boolean;
  disableBackdropClick?: boolean;
  customIcon?: React.ReactNode;
}

export interface UseConfirmDialogProps {
  title?: string;
  message: string | React.ReactNode;
  actions: ConfirmAction[];
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  showCloseButton?: boolean;
  disableBackdropClick?: boolean;
  customIcon?: React.ReactNode;
}

// BASE COMPONENT
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  title,
  message,
  actions,
  maxWidth = "sm",
  showCloseButton = true,
  disableBackdropClick = false,
  customIcon,
}) => {
  const handleBackdropClick = (event: {}, reason: string) => {
    if (disableBackdropClick && reason === "backdropClick") {
      return;
    }
    onClose();
  };

  const handleActionClick = (action: ConfirmAction) => {
    action.onClick();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleBackdropClick}
      maxWidth={maxWidth}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      {(title || showCloseButton) && (
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {customIcon && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {customIcon}
              </Box>
            )}
            <Typography variant="h6" sx={{ flex: 1 }}>
              {title}
            </Typography>
            {showCloseButton && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: "grey.500" }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      )}

      <DialogContent sx={{ pt: title ? 1 : 3 }}>
        {typeof message === "string" ? (
          <Typography>{message}</Typography>
        ) : (
          <Box>{message}</Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant || "outlined"}
            color={action.color || "primary"}
            startIcon={action.startIcon}
            endIcon={action.endIcon}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled}
            sx={action.sx}
          >
            {action.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

// CUSTOM HOOK - FIXED VERSION
export const useConfirmDialog = () => {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    props: UseConfirmDialogProps | null;
  }>({
    open: false,
    props: null,
  });

  const showConfirm = useCallback((props: UseConfirmDialogProps) => {
    setDialogState({
      open: true,
      props,
    });
  }, []);

  const hideConfirm = useCallback(() => {
    setDialogState({
      open: false,
      props: null,
    });
  }, []);

  const DialogComponent = () => {
    if (!dialogState.props) return null;

    return (
      <ConfirmDialog
        open={dialogState.open}
        onClose={hideConfirm}
        {...dialogState.props}
      />
    );
  };

  return {
    showConfirm,
    hideConfirm,
    ConfirmDialog: DialogComponent,
  };
};
